import { useCallback } from "react";
import { useToken } from "../../../../../server/auth/use-token";
import { authHeader } from "../../../../../utils/auth-header";
import type { ChannelName } from "../../../../channels/types";
import { selfDriving } from "../../../../self-driving/self-driving";
import type { CoPilotResponse } from "../../../../self-driving/types";
import type { CoPilotSchema } from "./forms/schema";

type RefineQuery = { prompt: string; context: CoPilotResponse["prompt"] };

export function useCoPilotQueries(
  channel: NonNullable<ChannelName>,
  mergeTags: string[],
): {
  query: (values: CoPilotSchema) => Promise<CoPilotResponse>;
  refineQuery: (query: RefineQuery) => Promise<CoPilotResponse>;
  refineSection: (
    selection: { section: string; full: string },
    prompt: string,
  ) => Promise<CoPilotResponse>;
} {
  const getToken = useToken();

  const query = useCallback(
    async (values: CoPilotSchema) => {
      const { data, error } = await selfDriving.POST("/moment", {
        body: { sendMethod: channel, mergeTags, ...values },
        ...authHeader(await getToken()),
        params: { query: { model: "alpha" } },
      });

      if (error || !data) throw new Error(error.error || "Request failed");

      return data;
    },
    [channel, mergeTags, getToken],
  );

  const refineQuery = useCallback(
    async ({ prompt, context }: RefineQuery) => {
      const { data, error } = await selfDriving.POST("/moment/refine", {
        body: { prompt, context },
        ...authHeader(await getToken()),
        params: { query: { model: "alpha" } },
      });

      if (error || !data) throw new Error(error.error || "Request failed");

      return data;
    },
    [getToken],
  );

  const refineSection = useCallback(
    async (selection: { section: string; full: string }, prompt: string) => {
      const { data, error } = await selfDriving.POST("/moment/refine/section", {
        body: { prompt, sendMethod: channel, mergeTags, ...selection },
        ...authHeader(await getToken()),
        params: { query: { model: "alpha" } },
      });

      if (error || !data) throw new Error(error.error || "Request failed");

      return data;
    },
    [channel, mergeTags, getToken],
  );

  return { query, refineQuery, refineSection };
}
